import { ClientInferResponseBody } from "@ts-rest/core";
import z from "zod";

import {
  patientsApiContract,
  UpdateWorkRequirementScreeningBodyType,
  workRequirementExemptionEnumSchema,
  WorkRequirementMemberDetailType,
  WorkRequirementSlotType,
} from "@libs/api/patients";
import { HTTPStatusCode } from "@libs/api/types";
import { I18nKeyType } from "@/i18n";

export type WorkRequirementType = ClientInferResponseBody<
  typeof patientsApiContract.workRequirements.getWorkRequirement,
  typeof HTTPStatusCode.OK
>;
export type { WorkRequirementMemberDetailType, WorkRequirementSlotType };

export type WorkRequirementExemptionType = z.infer<
  typeof workRequirementExemptionEnumSchema
>;
export type WorkRequirementScreeningMemberType =
  UpdateWorkRequirementScreeningBodyType["members"][number];

type WorkRequirementExemptionGroupType = {
  i18nKey: I18nKeyType;
  exemptions: WorkRequirementExemptionType[];
};

export const WORK_REQUIREMENT_EXEMPTION_GROUPS: WorkRequirementExemptionGroupType[] =
  [
    {
      i18nKey: "native:workRequirements.exemptions.groups.CAREGIVING",
      exemptions: [
        "CAREGIVER_OF_CHILD_13_OR_YOUNGER",
        "CAREGIVER_OF_PERSON_WITH_DISABILITY",
      ],
    },
    {
      i18nKey: "native:workRequirements.exemptions.groups.HEALTH_AND_RECOVERY",
      exemptions: [
        "PREGNANT_OR_POSTPARTUM",
        "MEDICALLY_FRAIL",
        "IN_DRUG_OR_ALCOHOL_TREATMENT",
      ],
    },
    {
      i18nKey:
        "native:workRequirements.exemptions.groups.BENEFITS_AND_COVERAGE",
      exemptions: [
        "MEETING_SNAP_TANF_WORK_REQUIREMENTS",
        "NOT_ENROLLED_OR_ELIGIBLE_FOR_MEDICARE",
      ],
    },
    {
      i18nKey: "native:workRequirements.exemptions.groups.LIFE_SITUATIONS",
      exemptions: [
        "FOSTER_YOUTH_UNDER_26",
        "TRIBAL_MEMBER",
        "VETERAN_100_PERCENT_DISABILITY",
        "INCARCERATED_OR_RELEASED_LAST_90_DAYS",
      ],
    },
  ];

export const getExemptionI18nKey = (exemption: WorkRequirementExemptionType) =>
  `native:workRequirements.exemptions.options.${exemption}` as I18nKeyType;

const hoursFieldSchema = z
  .string()
  .regex(/^\d*(\.\d{0,2})?$/)
  .optional();

export const workRequirementHoursFormSchema = z.object({
  employmentHours: hoursFieldSchema,
  employmentMonthlyIncome: z.string().optional(),
  jobTrainingHours: hoursFieldSchema,
  schoolHours: hoursFieldSchema,
  communityServiceHours: hoursFieldSchema,
});
export type WorkRequirementHoursFormType = z.infer<
  typeof workRequirementHoursFormSchema
>;

export const workRequirementCaseNumbersFormSchema = z.record(
  z.string(),
  z.string().trim(),
);
export type WorkRequirementCaseNumbersFormType = z.infer<
  typeof workRequirementCaseNumbersFormSchema
>;

// The patient's latest answers for every member being checked, in the shape
// the screening endpoint expects, so each step can resubmit them unchanged
// apart from its own edits.
export const toScreeningMembers = (
  workRequirement: WorkRequirementType,
): WorkRequirementScreeningMemberType[] =>
  workRequirement.members.map(member => ({
    householdMemberId: member.householdMemberId,
    exemptions: member.exemptions,
    hasQualifyingIncome: member.hasQualifyingIncome,
    employmentHours: member.employmentHours,
    employmentMonthlyIncome: member.employmentMonthlyIncome,
    jobTrainingHours: member.jobTrainingHours,
    schoolHours: member.schoolHours,
    communityServiceHours: member.communityServiceHours,
  }));

// Members asked about income: everyone not already exempt.
export const getMembersNeedingIncome = <
  TMember extends Pick<WorkRequirementScreeningMemberType, "exemptions">,
>(
  members: TMember[],
) => members.filter(member => member.exemptions.length === 0);

// Members asked for hours: not exempt and not earning the income threshold.
const getMembersNeedingHours = <
  TMember extends Pick<
    WorkRequirementScreeningMemberType,
    "exemptions" | "hasQualifyingIncome"
  >,
>(
  members: TMember[],
) =>
  getMembersNeedingIncome(members).filter(
    member => !member.hasQualifyingIncome,
  );

type WorkRequirementStepPathnameType =
  | "/work-requirement/[id]/who"
  | "/work-requirement/[id]/exemptions"
  | "/work-requirement/[id]/income"
  | "/work-requirement/[id]/case-numbers"
  | "/work-requirement/[id]/next-steps"
  | "/work-requirement/[id]/confirmation";

type WorkRequirementStepLinkType =
  | { pathname: WorkRequirementStepPathnameType; params: { id: string } }
  | {
      pathname: "/work-requirement/[id]/hours/[householdMemberId]";
      params: { id: string; householdMemberId: string };
    };

// Where the screening continues after `step`, skipping steps nobody needs: no
// income question when everyone is exempt, no hours for members who are
// exempt or already earn enough. `step` null means the screening hasn't started.
export const getNextWorkRequirementStepLink = ({
  workRequirementId,
  step,
  members,
  householdMemberId,
}: {
  workRequirementId: string;
  step: WorkRequirementType["screeningLastStep"];
  members: Pick<
    WorkRequirementScreeningMemberType,
    "householdMemberId" | "exemptions" | "hasQualifyingIncome"
  >[];
  // For the HOURS step: the member whose hours were just reported.
  householdMemberId?: string;
}): WorkRequirementStepLinkType => {
  const params = { id: workRequirementId };
  const nextStepPath = (
    pathname: WorkRequirementStepPathnameType,
  ): WorkRequirementStepLinkType => ({ pathname, params });

  const membersNeedingHours = getMembersNeedingHours(members);
  const hoursStepFor = (index: number): WorkRequirementStepLinkType => {
    const member = membersNeedingHours[index];
    return member
      ? {
          pathname: "/work-requirement/[id]/hours/[householdMemberId]",
          params: { ...params, householdMemberId: member.householdMemberId },
        }
      : nextStepPath("/work-requirement/[id]/case-numbers");
  };

  switch (step) {
    case null:
      return nextStepPath("/work-requirement/[id]/who");
    case "WHO":
      return nextStepPath("/work-requirement/[id]/exemptions");
    case "EXEMPTIONS":
      return getMembersNeedingIncome(members).length > 0
        ? nextStepPath("/work-requirement/[id]/income")
        : nextStepPath("/work-requirement/[id]/case-numbers");
    case "INCOME":
      return hoursStepFor(0);
    case "HOURS": {
      const currentIndex = membersNeedingHours.findIndex(
        member => member.householdMemberId === householdMemberId,
      );
      return hoursStepFor(currentIndex + 1);
    }
    case "CASE_NUMBERS":
      return nextStepPath("/work-requirement/[id]/next-steps");
    case "NEXT_STEPS":
      return nextStepPath("/work-requirement/[id]/confirmation");
  }
};
